import { useState } from "react";
import { Form, required, useLogin, useNotify, useTranslate } from "ra-core";
import { useQueryClient } from "@tanstack/react-query";
import { useForm, type SubmitHandler, type FieldValues } from "react-hook-form";
import { Link, useNavigate } from "react-router";
import { Eye, EyeOff } from "lucide-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faGoogle } from "@fortawesome/free-brands-svg-icons";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { TextInput } from "@/components/admin/text-input";
import { Notification } from "@/components/admin/notification";
import { useConfigurationContext } from "@/components/crm/root/ConfigurationContext.tsx";
import { AuthVisualPanel } from "./AuthVisualPanel";
import { register as authRegister } from "@/providers/firebase/authProvider";
import { loadSavedTasteJob } from "@/services/api/guestTasteApi";
import { logger } from "@/utils/logger";
import { consumePostLoginPath } from "@/utils/postLoginPath";
import { RaisyLoadingPage } from "@/components/loading/RaisyLoadingPage";

// glowtest-aligned email validation (authenticate.js validateEmail)
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * THE GOOGLE MARK, TAKEN FROM THE INTEGRATIONS TAB RATHER THAN MINTED AGAIN.
 *
 * The Integrations page already names Google with FontAwesome's brand glyph in the Google
 * brand blue (`components/integrations/IntegrationProviders.tsx` -> `faGoogle` /
 * `#4285F4`), so the doorway draws the SAME mark from the SAME source. One provider, one
 * mark: a second Google look-alike (a hand-drawn G, a single-colour lucide icon, an
 * Ant-Design outline) would read as a different Google from the one on the Integrations tab
 * (PO 2026-09-26: "the google icon. we have it already in 'integrations' tab").
 */
const GOOGLE_BRAND_COLOR = "#4285F4";

interface RegisterFormValues {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
}

const REGISTER_ERROR_MESSAGES: Record<string, string> = {
  "auth/email-already-in-use":
    "An account with this email already exists. Try signing in instead.",
  "auth/invalid-email": "Invalid email format",
  "auth/weak-password": "Password must be at least 8 characters",
  "auth/operation-not-allowed": "Email registration is not enabled",
};

/**
 * Login page displayed when authentication is enabled and the user is not authenticated.
 *
 * Login/Register tabs mirror glowtest's Register UX: firstName/lastName (required),
 * email with regex validation, ≥8-char password with show/hide toggle. Registration
 * goes through authProvider.register() and lands on /entities — raisy has no credit
 * system, so glowtest's /registration-success redirect is intentionally not ported.
 *
 * Automatically shown when an unauthenticated user tries to access a protected route.
 * Handles login via authProvider.login() and displays error notifications on failure.
 *
 * @see {@link https://marmelab.com/shadcn-admin-kit/docs/loginpage LoginPage documentation}
 * @see {@link https://marmelab.com/shadcn-admin-kit/docs/security Security documentation}
 */
export const LoginPage = (props: { redirectTo?: string }) => {
  const { googleWorkplaceDomain, disableEmailPasswordAuthentication } =
    useConfigurationContext();
  const { redirectTo } = props;
  const [loading, setLoading] = useState(false);
  /**
   * WHICH TAB IS OPEN WHEN THE PAGE APPEARS — decided by the ARRIVAL, not by a majority
   * (@P216 D-001, 2026-09-26).
   *
   * A visitor holding an UNCLAIMED doorway run is new BY CONSTRUCTION: the run he is carrying
   * has never been claimed, and claiming it is what creates the company. For him "Sign in" is
   * the wrong form first — a form that presupposes the account he does not have yet — so the
   * page opens on Register, which is the form his situation actually needs. The draft count he
   * was promised is waiting on the other side of it.
   *
   * Every OTHER arrival (a bookmark, a typed URL, an expired session bouncing him off a page he
   * was reading) is genuinely unknown, and the safe default for an unknown is Sign in: a wrong
   * Sign in costs one tab click, while a wrong Register asks a returning user to fill four fields
   * and can answer "an account with this email already exists".
   *
   * The signal is read ONCE, from the run in hand (never `consumePostLoginPath`, which is
   * one-shot and would eat the destination the submit still has to read).
   */
  const runWaiting = Boolean(loadSavedTasteJob()?.claimToken);
  const [activeTab, setActiveTab] = useState<"login" | "register">(
    runWaiting ? "register" : "login",
  );
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const login = useLogin();
  const notify = useNotify();
  const translate = useTranslate();
  const queryClient = useQueryClient();

  /**
   * Where to land after a successful sign-in: an explicit redirect wins, else the
   * page he was trying to open before signing in (see utils/postLoginPath), else
   * the default surface. Read once per submit — consuming it twice would clear it
   * before the login call could use it.
   */
  const afterAuthPath = () =>
    redirectTo ?? consumePostLoginPath() ?? "/entities";

  const {
    register: registerField,
    handleSubmit: handleRegisterSubmit,
    formState: { errors: registerErrors, isValid: isRegisterValid },
  } = useForm<RegisterFormValues>({ mode: "onChange" });

  const handleSubmit: SubmitHandler<FieldValues> = (values) => {
    const start = performance.now();
    const destination = afterAuthPath();
    logger.info("[LOGIN][handleSubmit_010] sign-in clicked", { redirect_to: destination });
    setLoading(true);
    login(values, destination)
      .then(() => {
        logger.info("[LOGIN][handleSubmit_100] login promise resolved", { duration_ms: Math.round(performance.now() - start) });
        setLoading(false);
      })
      .catch((error) => {
        logger.error("[LOGIN][handleSubmit_090] login promise rejected", error instanceof Error ? error : undefined);
        setLoading(false);
        notify(
          typeof error === "string"
            ? error
            : typeof error === "undefined" || !error.message
              ? "ra.auth.sign_in_error"
              : error.message,
          {
            type: "error",
            messageArgs: {
              _:
                typeof error === "string"
                  ? error
                  : error && error.message
                    ? error.message
                    : undefined,
            },
          },
        );
      });
  };

  const onRegister: SubmitHandler<RegisterFormValues> = (values) => {
    const start = performance.now();
    const destination = afterAuthPath();
    logger.info("[REGISTER][handleSubmit_010] register clicked", { redirect_to: destination });
    setLoading(true);
    authRegister(values)
      .then(() => {
        logger.info("[REGISTER][handleSubmit_100] register resolved", { duration_ms: Math.round(performance.now() - start) });
        // Mirror useLogin's post-login bookkeeping so RA's auth gates re-check.
        queryClient.invalidateQueries({ queryKey: ["auth"] });
        setLoading(false);
        navigate(destination);
      })
      .catch((error: unknown) => {
        logger.error("[REGISTER][handleSubmit_090] register rejected", error instanceof Error ? error : undefined);
        setLoading(false);
        const code = (error as { code?: string })?.code;
        notify(REGISTER_ERROR_MESSAGES[code ?? ""] ?? "Registration failed. Please try again.", {
          type: "error",
        });
      });
  };

  const googleSignIn = () => {
    const start = performance.now();
    const destination = afterAuthPath();
    logger.info("[LOGIN][googleClick_010] google sign-in clicked", { redirect_to: destination });
    setLoading(true);
    login({ provider: "google" }, destination)
      .then(() => {
        logger.info("[LOGIN][googleClick_100] google login resolved", { duration_ms: Math.round(performance.now() - start) });
        setLoading(false);
      })
      .catch((error: unknown) => {
        setLoading(false);
        const message =
          error instanceof Error ? error.message : "Google sign-in failed";
        notify(message, { type: "error" });
      });
  };

  return (
    <div className="min-h-screen flex">
      <div className="relative grid w-full lg:grid-cols-2">
        <AuthVisualPanel />
        <div className="flex flex-col justify-center w-full p-4 lg:p-8">
          <div className="w-full space-y-6 lg:mx-auto lg:w-[350px]">
            <Tabs
              value={activeTab}
              onValueChange={(value) => setActiveTab(value as "login" | "register")}
              className="w-full"
            >
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="login">
                  {translate("ra.auth.sign_in", { _: "Sign in" })}
                </TabsTrigger>
                <TabsTrigger value="register">
                  {translate("crm.auth.register.tab", { _: "Register" })}
                </TabsTrigger>
              </TabsList>

              {/*
               * THE FASTEST PATH IS THE FIRST THING SEEN (PO 2026-09-26: *"yeah this is more
               * lean than what you have here"*, over glowtest's dialog, whose order is
               * Google → OR → fields).
               *
               * The order used to be fields → Sign in → Forgot password → OR → Google, so the
               * one action that needs NO typing sat under a two-field form and its divider: the
               * eye had to pass four things before reaching the option that skips all of them.
               * Same elements, same copy, same handlers — only the order changed. It renders
               * between the tab list and whichever panel is open, so it belongs to BOTH tabs
               * exactly as before, and nothing was removed to make room: Forgot password,
               * Request access and the visual panel are all still here.
               */}
              <div className="mt-6 space-y-4">
                <Button
                  type="button"
                  variant="outline"
                  className="w-full cursor-pointer"
                  disabled={loading}
                  onClick={googleSignIn}
                >
                  {loading ? (
                    <Spinner
                      className="text-primary-foreground size-4"
                      data-icon="inline-start"
                    />
                  ) : (
                    <FontAwesomeIcon
                      icon={faGoogle}
                      className="size-4"
                      aria-hidden
                      style={{ color: GOOGLE_BRAND_COLOR }}
                      data-icon="inline-start"
                    />
                  )}
                  {/*
                   * A LABEL THAT NAMES ONE OUTCOME IS WRONG ON THE OTHER TAB (P216 D-005).
                   * One handler serves both tabs — `googleSignIn` -> authProvider's
                   * `signInWithPopup` — so whether this SIGNS IN or CREATES the account is decided
                   * by whether the account exists, never by which tab is open; and D-001 makes
                   * Register the arrival tab for a guest holding an unclaimed run, i.e. the common
                   * case. "Sign in with Google" therefore announces the wrong action exactly where
                   * most first-time visitors meet it, and a per-tab "Sign up with Google" would be
                   * equally wrong for a returning visitor on this tab.
                   * "Continue with Google" is Google's OWN wording, not a coinage: the Sign in with
                   * Google HTML reference lists exactly four `data-text` values — signin_with,
                   * signup_with, continue_with, signin — and `continue_with` is the only one that
                   * names no outcome (read 2026-09-28, page updated 2026-09-01).
                   */}
                  Continue with Google
                </Button>
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-background px-2 text-muted-foreground">
                      Or
                    </span>
                  </div>
                </div>
              </div>

              <TabsContent value="login" className="space-y-6">
                {disableEmailPasswordAuthentication ? null : (
                  <Form className="space-y-8" onSubmit={handleSubmit}>
                    <TextInput
                      label="ra.auth.email"
                      source="email"
                      type="email"
                      validate={required()}
                    />
                    <TextInput
                      label="ra.auth.password"
                      source="password"
                      type="password"
                      validate={required()}
                    />
                    <div className="flex flex-col gap-4">
                      <Button
                        type="submit"
                        className="cursor-pointer"
                        disabled={loading}
                      >
                        {loading ? (
                          <Spinner
                            className="text-primary-foreground size-4"
                            data-icon="inline-start"
                          />
                        ) : null}
                        {translate("ra.auth.sign_in")}
                      </Button>
                    </div>
                  </Form>
                )}
                {disableEmailPasswordAuthentication ? null : (
                  <Link
                    to={"/forgot-password"}
                    className="block text-sm text-center hover:underline"
                  >
                    {translate("crm.auth.forgot_password", {
                      _: "Forgot password?",
                    })}
                  </Link>
                )}
              </TabsContent>

              <TabsContent value="register" className="space-y-4">
                {disableEmailPasswordAuthentication ? null : (
                  <form
                    onSubmit={handleRegisterSubmit(onRegister)}
                    className="space-y-4"
                    noValidate
                  >
                    <div className="flex flex-col gap-2">
                      <Label htmlFor="firstName">
                        {translate("crm.auth.first_name", { _: "First name" })}
                      </Label>
                      <Input
                        id="firstName"
                        type="text"
                        autoComplete="given-name"
                        {...registerField("firstName", { required: "First name is required" })}
                      />
                      {registerErrors.firstName ? (
                        <p className="text-sm text-destructive">
                          {registerErrors.firstName.message}
                        </p>
                      ) : null}
                    </div>
                    <div className="flex flex-col gap-2">
                      <Label htmlFor="lastName">
                        {translate("crm.auth.last_name", { _: "Last name" })}
                      </Label>
                      <Input
                        id="lastName"
                        type="text"
                        autoComplete="family-name"
                        {...registerField("lastName", { required: "Last name is required" })}
                      />
                      {registerErrors.lastName ? (
                        <p className="text-sm text-destructive">
                          {registerErrors.lastName.message}
                        </p>
                      ) : null}
                    </div>
                    <div className="flex flex-col gap-2">
                      <Label htmlFor="register-email">
                        {translate("ra.auth.email", { _: "Email" })}
                      </Label>
                      <Input
                        id="register-email"
                        type="email"
                        autoComplete="email"
                        {...registerField("email", {
                          required: "Email is required",
                          validate: (value) =>
                            EMAIL_REGEX.test(value) || "Invalid email format",
                        })}
                      />
                      {registerErrors.email ? (
                        <p className="text-sm text-destructive">
                          {registerErrors.email.message}
                        </p>
                      ) : null}
                    </div>
                    <div className="flex flex-col gap-2">
                      <Label htmlFor="register-password">
                        {translate("ra.auth.password", { _: "Password" })}
                      </Label>
                      <div className="relative">
                        <Input
                          id="register-password"
                          type={showPassword ? "text" : "password"}
                          autoComplete="new-password"
                          className="pr-10"
                          {...registerField("password", {
                            required: "Password is required",
                            minLength: {
                              value: 8,
                              message: "Password must be at least 8 characters",
                            },
                          })}
                        />
                        <button
                          type="button"
                          aria-label="toggle password visibility"
                          onClick={() => setShowPassword((prev) => !prev)}
                          className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-foreground cursor-pointer"
                        >
                          {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                      </div>
                      {registerErrors.password ? (
                        <p className="text-sm text-destructive">
                          {registerErrors.password.message}
                        </p>
                      ) : null}
                    </div>
                    <Button
                      type="submit"
                      className="w-full cursor-pointer"
                      disabled={loading || !isRegisterValid}
                    >
                      {loading ? (
                        <Spinner
                          className="text-primary-foreground size-4"
                          data-icon="inline-start"
                        />
                      ) : null}
                      {translate("crm.auth.register.submit", { _: "Register" })}
                    </Button>
                  </form>
                )}
              </TabsContent>
            </Tabs>

            <button
              type="button"
              onClick={() =>
                setActiveTab(activeTab === "login" ? "register" : "login")
              }
              className="block text-sm text-center hover:underline w-full cursor-pointer"
            >
              {activeTab === "login"
                ? translate("crm.auth.no_account", {
                    _: "Don't have an account? Register",
                  })
                : translate("crm.auth.has_account", {
                    _: "Already have an account? Sign in",
                  })}
            </button>
            <Link
              to={"/request-access"}
              className="block text-sm text-center hover:underline text-muted-foreground"
            >
              {translate("crm.auth.request_access.link", {
                _: "Request access",
              })}
            </Link>
          </div>
        </div>
      </div>
      <Notification />
      {loading && <RaisyLoadingPage />}
    </div>
  );
};
